import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, MessagesSquare, Send, Ticket } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useFaq, useSupportContacts } from "@/hooks/useCatalog";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const LEGAL_SECTIONS = [
  {
    id: "cgv",
    title: "Conditions générales",
    text: "Les licences vendues sont destinées à un usage légitime. Toute activation frauduleuse entraîne l'annulation de la garantie. Les prix affichés sont en FCFA (conversion EUR/USD indicative).",
  },
  {
    id: "confidentialite",
    title: "Politique de confidentialité",
    text: "Vos données personnelles (nom, email, téléphone) servent uniquement au traitement des commandes et au support. Elles ne sont jamais revendues. Les mots de passe sont chiffrés (hachage bcrypt).",
  },
  {
    id: "remboursement",
    title: "Politique de remboursement",
    text: "Un remboursement est possible si la licence n'a pas été activée et dans le délai indiqué sur la fiche produit (de 2 jours à 1 semaine selon le produit). Les services techniques déjà réalisés ne sont pas remboursables.",
  },
  {
    id: "livraison",
    title: "Livraison numérique",
    text: "Les clés sont livrées instantanément après confirmation du paiement, affichées à l'écran et disponibles dans « Mes licences ». Les prestations de service sont traitées sous 24 à 72 heures.",
  },
];

export default function Support() {
  const { data: faqs = [] } = useFaq();
  const { data: contacts } = useSupportContacts();
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: "", category: "general", order_number: "", message: "", name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/settings/legal-info")
      .then(({ data }) => active && setLegal(data))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const legalSections = legal?.sections || LEGAL_SECTIONS;
  const aboutTitle = legal?.about_title || "ISMO-TECH en quelques mots";
  const aboutText = legal?.about_text ||
    "ISMO-TECH est une boutique spécialisée dans la vente de licences numériques, logiciels, abonnements, outils professionnels et services informatiques autorisés. Notre priorité : la clarté sur l'origine des licences, la rapidité de livraison et un support humain accessible.";

  useSeo({
    title: "Support & FAQ — ISMO-TECH",
    description: "Besoin d'aide ? WhatsApp, email, chat, ticket support et FAQ ISMO-TECH. Réponse 7j/7 de 08h à 22h.",
  });

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const payload = { ...form };
      if (user) {
        delete payload.email;
        delete payload.name;
      }
      const { data } = await api.post("/tickets", payload);
      setSent(data.reference);
      setForm({ subject: "", category: "general", order_number: "", message: "", name: "", email: "" });
      toast.success(`Ticket ${data.reference} créé`);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSending(false);
    }
  };

  const whatsappLink = `https://wa.me/${(contacts?.whatsapp || "").replace(/[^0-9]/g, "")}`;

  return (
    <div className="pb-6">
      <section className="hero-shell relative overflow-hidden">
        <div className="hero-grid-lines absolute inset-0 opacity-60" />
        <div className="container-it relative z-10 py-12 lg:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Support client</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-white sm:text-5xl" data-testid="support-title">
            Besoin d'aide ?
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
            Notre équipe répond {contacts?.hours || "7j/7 de 08h à 22h"}. Choisissez le canal qui vous convient.
          </p>
        </div>
      </section>

      <section className="container-it -mt-8 relative z-20">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: MessageCircle, label: "WhatsApp", value: "+225 01 40 45 69 48", href: whatsappLink, testId: "support-whatsapp" },
            { icon: Mail, label: "Email", value: contacts?.email || "Cismo267@gmail.com", href: `mailto:${contacts?.email || "Cismo267@gmail.com"}`, testId: "support-email" },
            { icon: MessagesSquare, label: "Chat direct", value: "Réponse < 5 min", href: whatsappLink, testId: "support-chat" },
            { icon: Ticket, label: "Ticket support", value: "Suivi écrit", href: "#ticket", testId: "support-ticket-link" },
          ].map(({ icon: Icon, label, value, href, testId }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={testId}
              className="group rounded-2xl border border-line bg-white p-5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand">
                <Icon className="h-5 w-5" />
              </span>
              <p className="mt-4 font-display text-base font-bold text-ink">{label}</p>
              <p className="mt-1 text-xs text-slate-500">{value}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="container-it grid gap-8 py-12 lg:grid-cols-[1.1fr_1fr] lg:py-16" id="faq">
        <div>
          <p className="label-eyebrow mb-2">FAQ</p>
          <h2 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {faqs.map((item, i) => (
              <AccordionItem key={item.id} value={item.id} className="border-b border-line">
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left text-sm font-bold text-ink hover:text-brand">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div id="ticket">
          <div className="rounded-2xl border border-line bg-white p-6">
            <p className="font-display text-lg font-bold text-ink">Ouvrir un ticket support</p>
            <p className="mt-1 text-xs text-slate-500">
              Décrivez votre demande : nous répondons par email et dans votre espace client.
            </p>

            {sent && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="ticket-success">
                Ticket <span className="font-bold">{sent}</span> créé. Notre équipe vous répond rapidement.
              </div>
            )}

            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="ticket-form">
              {!user && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="ticket-name"
                    required
                    placeholder="Votre nom"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
                  />
                  <input
                    data-testid="ticket-email"
                    required
                    type="email"
                    placeholder="Votre email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              )}
              <input
                data-testid="ticket-subject"
                required
                minLength={3}
                placeholder="Sujet de votre demande"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <select
                  data-testid="ticket-category"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
                >
                  <option value="general">Question générale</option>
                  <option value="commande">Ma commande</option>
                  <option value="activation">Activation de licence</option>
                  <option value="paiement">Paiement</option>
                  <option value="deblocage">Déblocage & services</option>
                  <option value="remboursement">Remboursement</option>
                </select>
                <input
                  data-testid="ticket-order"
                  placeholder="N° de commande (optionnel)"
                  value={form.order_number}
                  onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <textarea
                data-testid="ticket-message"
                required
                minLength={5}
                rows={5}
                placeholder="Expliquez votre problème en détail..."
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="w-full rounded-xl border border-line p-4 text-sm focus:border-brand focus:outline-none"
              />
              <button
                type="submit"
                data-testid="ticket-submit"
                disabled={sending}
                className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer ma demande
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="container-it pb-8" id="apropos">
        <div className="rounded-2xl border border-line bg-surface p-6 sm:p-10">
          <p className="label-eyebrow mb-2">À propos & informations légales</p>
          <h2 className="font-display text-2xl font-extrabold text-ink" data-testid="legal-about-title">{aboutTitle}</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600" data-testid="legal-about-text">
            {aboutText}
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {legalSections.map((section) => (
              <div key={section.id} id={section.id} className="rounded-xl border border-line bg-white p-5" data-testid={`legal-section-${section.id}`}>
                <p className="font-display text-base font-bold text-ink">{section.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{section.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
